'use client';

import React from 'react';

import { Button, Typography } from 'lighthouse';

import Reveal from '@/widgets/ServiceLanding/Reveal';

import { useIsMobile } from '@/hooks/common/useMobile';

import { handleMobileFormCtaClick } from '@/utilities/handleMobileFormCtaClick';

import { useSupportStore } from '@/stores/support/useSupportStore';

import { FinalCtaProps } from './models';

import { ArrowLeft2 } from 'iconsax-react';

const badgeToneClassName = {
    sky: 'border-sky-400/30 bg-sky-400/10 text-sky-300',
    emerald: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
};

const blobToneClassName = {
    sky: 'bg-sky-400/20',
    emerald: 'bg-emerald-400/20',
    cyan: 'bg-cyan-400/20'
};

const FinalCta = ({
    badge,
    badgeTone = 'sky',
    title,
    subtitle,
    ctaLabel,
    ctaHref,
    mobileOpenEvent,
    onCtaClick,
    startIcon,
    endIcon = <ArrowLeft2 size='18' color='currentColor' />,
    footnote,
    relatedLink,
    sectionClassName = 'desktop-width px-4 pb-20 pt-4',
    blobTone = 'cyan'
}: FinalCtaProps) => {
    const isMobile = useIsMobile();
    const openSupport = useSupportStore((store) => store.openSupport);

    const handleCtaClick = (event: React.MouseEvent<HTMLElement>) => {
        if (mobileOpenEvent) {
            handleMobileFormCtaClick(event, isMobile, mobileOpenEvent);
        }

        onCtaClick?.();
    };

    return (
        <section className={sectionClassName}>
            <Reveal>
                <div className='relative overflow-hidden rounded-lg bg-slate-950 px-6 py-14 text-center md:px-12 md:py-20'>
                    <div className='pointer-events-none absolute inset-0'>
                        <div className='absolute -top-24 right-1/3 h-72 w-72 rounded-full bg-sky-500/25 blur-3xl' />
                        <div className={`absolute -bottom-24 left-1/4 h-72 w-72 rounded-full blur-3xl ${blobToneClassName[blobTone]}`} />
                        <div
                            className='hero-radar absolute right-1/2 top-1/2 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full border
                                border-sky-400/15'
                        />
                    </div>

                    <div className='relative'>
                        <Typography
                            variant='caption'
                            component='span'
                            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 font-medium ${badgeToneClassName[badgeTone]}`}>
                            {badge}
                        </Typography>
                        <Typography
                            variant='h5'
                            component='h2'
                            color='whiteHighEmphasis'
                            className='mx-auto mt-5 max-w-2xl font-black leading-[1.6] text-white md:text-4xl md:leading-[1.6]'>
                            {title}
                        </Typography>
                        <Typography
                            variant='body2'
                            component='p'
                            color='whiteMediumEmphasis'
                            className='mx-auto mt-2 max-w-xl leading-7 text-white/70'>
                            {subtitle}
                        </Typography>
                        <Button
                            {...(ctaHref
                                ? { component: 'a' as const, href: ctaHref, onClick: handleCtaClick }
                                : { onClick: handleCtaClick, className: 'mt-8 cursor-pointer text-white' })}
                            variant='contained'
                            color='primary'
                            size='large'
                            width='wide'
                            startIcon={startIcon}
                            endIcon={endIcon}
                            className={ctaHref ? 'mt-8 text-white' : 'mt-8 cursor-pointer text-white'}>
                            {ctaLabel}
                        </Button>
                        {footnote && (
                            <Typography variant='overline' component='p' className='mt-4 text-white/50'>
                                {footnote}
                            </Typography>
                        )}
                        {relatedLink && (
                            <Typography variant='caption' component='p' className='mt-6 text-white/60'>
                                {relatedLink.label}{' '}
                                {relatedLink.support ? (
                                    <button
                                        type='button'
                                        onClick={() => openSupport(relatedLink.support!.source, relatedLink.support!.categoryId)}
                                        className='all-unset inline cursor-pointer font-medium text-sky-300 underline underline-offset-4
                                            hover:text-sky-200'>
                                        {relatedLink.cta}
                                    </button>
                                ) : (
                                    <a href={relatedLink.href} className='font-medium text-sky-300 underline underline-offset-4 hover:text-sky-200'>
                                        {relatedLink.cta}
                                    </a>
                                )}
                            </Typography>
                        )}
                    </div>
                </div>
            </Reveal>
        </section>
    );
};

export default FinalCta;
